import React, { ReactNode, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import {
    GET_BUSINESS_CODE,
    GET_COMPANY,
    GET_EMPLOYEE,
    GET_INCORP_YEAR_ALL,
    GET_INCORP_YEAR_DETAIL_ALL,
} from './reports.graphql';
import {
    Box,
    Breadcrumbs,
    Button,
    Card,
    CardActionArea,
    CardContent,
    Chip,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import { nbNO } from '@mui/x-data-grid-pro/locales';
import {
    ArrowBack,
    CalendarMonth,
    Download,
    Groups,
    SettingsEthernet,
} from '@mui/icons-material';
import LoadingSpinner from '../components/LoadingSpinner';
import XLSX from 'xlsx';
import { Code, CompanyModel, EmployeeModel, IncorporationYearCompanyModel, IncorporationYearModel } from '../interfaces';
import ExcelLogo from '../assets/microsoft-excel.svg?react';

type BrregSection = 'businessCode' | 'employees' | 'incorporation';

const FILTER_OPTIONS = [
    { value: 'all', label: 'Ingen' },
    { value: '9', label: 'BFS' },
    { value: '16', label: 'BVN' },
];

const gridLocaleText = nbNO.components.MuiDataGrid.defaultProps.localeText;

export default function BrregSubMenu() {
    const [activeSection, setActiveSection] = useState<BrregSection | null>(null);
    const [filterOption, setFilterOption] = useState('all');

    if (!activeSection) {
        return <BrregOverviewCards onSelect={setActiveSection} />;
    }

    if (activeSection === 'businessCode') {
        return (
            <BusinessCodeReport
                filterOption={filterOption}
                onFilterChange={setFilterOption}
                onBackToOverview={() => setActiveSection(null)}
            />
        );
    }

    if (activeSection === 'employees') {
        return (
            <EmployeeReport
                allFlag={true}
                code={'ingen kode'}
                filterOption={filterOption}
                onFilterChange={setFilterOption}
                onBack={() => setActiveSection(null)}
            />
        );
    }

    return (
        <IncorporationYearReport filterOption={filterOption} onFilterChange={setFilterOption} onBack={() => setActiveSection(null)} />
    );
}

function BrregOverviewCards(props: { onSelect: (section: BrregSection) => void }) {
    const cards: { key: BrregSection; title: string; description: string; icon: ReactNode }[] = [
        {
            key: 'businessCode',
            title: 'Næringskode',
            description: 'Se fordeling per næringskode og gå videre til ansatte- og firmaoversikt.',
            icon: <SettingsEthernet />,
        },
        {
            key: 'employees',
            title: 'Antall ansatte',
            description: 'Se firma- og lisensfordeling per ansattintervall og drill ned til firmadetaljer.',
            icon: <Groups />,
        },
        {
            key: 'incorporation',
            title: 'Stiftelsesår',
            description: 'Analyser kunder etter stiftelsesår og åpne detaljoversikt for firmaer.',
            icon: <CalendarMonth />,
        },
    ];

    return (
        <Box>
            <Typography variant="h5" gutterBottom>
                BRREG rapporter
            </Typography>
            <Typography
                variant="body2"
                sx={{
                    color: "text.secondary",
                    mb: 3
                }}>
                Velg rapporttype for å utforske data i et stegvis arbeidsområde med tydelig drill-down.
            </Typography>
            <Grid container spacing={2}>
                {cards.map((card) => (
                    <Grid
                        key={card.key}
                        size={{
                            xs: 12,
                            md: 4
                        }}>
                        <Card variant="outlined">
                            <CardActionArea onClick={() => props.onSelect(card.key)}>
                                <CardContent>
                                    <Stack spacing={1.5}>
                                        <Stack direction="row" spacing={1} sx={{alignItems: 'center'}}>
                                            {card.icon}
                                            <Typography variant="h6">{card.title}</Typography>
                                        </Stack>
                                        <Typography variant="body2" sx={{
                                            color: "text.secondary"
                                        }}>
                                            {card.description}
                                        </Typography>
                                        <Box>
                                            <Button size="small">Åpne rapport</Button>
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </CardActionArea>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
}

interface ReportWorkspaceProps {
    title: string;
    subtitle: string;
    onBack: () => void;
    breadcrumb: string[];
    filterOption?: string;
    onFilterChange?: (value: string) => void;
    excelExport?: ReactNode;
    children: ReactNode;
}

function ReportWorkspace({
    title,
    subtitle,
    onBack,
    breadcrumb,
    filterOption,
    onFilterChange,
    excelExport,
    children,
}: ReportWorkspaceProps) {
    return (
        <Stack spacing={2}>
            <Paper elevation={1} sx={{ p: 2 }}>
                <Grid container spacing={2} sx={{alignItems: 'flex-start'}}>
                    <Grid
                        size={{
                            xs: 12,
                            md: 8
                        }}>
                        <Stack spacing={1.5}>
                            <Stack direction="row" spacing={1} sx={{alignItems: 'center'}}>
                                <IconButton onClick={onBack} title="Tilbake" size="large" sx={{ mt: -0.5 }}>
                                    <ArrowBack />
                                </IconButton>
                                <Box>
                                    <Typography variant="h6">{title}</Typography>
                                    <Typography variant="body2" sx={{
                                        color: "text.secondary"
                                    }}>
                                        {subtitle}
                                    </Typography>
                                </Box>
                            </Stack>

                            <Breadcrumbs aria-label="breadcrumb">
                                {breadcrumb.map((entry) => (
                                    <Chip key={entry} label={entry} size="small" />
                                ))}
                            </Breadcrumbs>
                        </Stack>
                    </Grid>

                    <Grid
                        size={{
                            xs: 12,
                            md: 4
                        }}>
                        <Stack
                            direction={{ xs: 'column', sm: 'row', md: 'column' }}
                            spacing={1.5}
                            sx={{ alignItems: { xs: 'stretch', sm: 'flex-end', md: 'flex-end' } }}>
                            {onFilterChange && filterOption !== undefined && (
                                <FormControl sx={{ minWidth: 220 }} size="small">
                                    <InputLabel id="brreg-filter-label">Filtrere på</InputLabel>
                                    <Select
                                        labelId="brreg-filter-label"
                                        id="brreg-filter-select"
                                        value={filterOption}
                                        label="Filtrere på"
                                        onChange={(event) => onFilterChange(event.target.value)}>
                                        {FILTER_OPTIONS.map((opt) => (
                                            <MenuItem key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}

                            {excelExport && (
                                <Stack direction="row" spacing={0.5} sx={{alignItems: 'center'}}>
                                    <Download fontSize="small" color="action" />
                                    {excelExport}
                                </Stack>
                            )}
                        </Stack>
                    </Grid>
                </Grid>
            </Paper>
            {children}
        </Stack>
    );
}

function BusinessCodeReport(props: { filterOption: string; onFilterChange: (value: string) => void; onBackToOverview: () => void }) {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCode, setSelectedCode] = useState<Code | null>(null);

    const { loading, data } = useQuery<{ businesses: Code[] }>(GET_BUSINESS_CODE, {
        variables: { filterOption: props.filterOption },
    });

    const rows = (data?.businesses ?? []).filter(
        (item) =>
            !searchTerm ||
            item.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.description.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    const columns = useMemo<GridColDef[]>(
        () => [
            {
                field: 'codeText',
                headerName: 'Næringskode',
                minWidth: 300,
                flex: 1.2,
                valueGetter: (_, row) => `${row.code} ${row.description}`,
            },
            {
                field: 'companyCount',
                headerName: 'Antall firma',
                minWidth: 140,
                flex: 0.8,
                renderCell: (params) => (
                    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-start' }}>
                        <Button
                            size="small"
                            onClick={() => setSelectedCode(params.row as Code)}
                            sx={{
                                minWidth: 0,
                                px: 0,
                                textDecoration: 'underline',
                                fontWeight: 600,
                            }}>
                            {params.value}
                        </Button>
                    </Box>
                ),
            },
            {
                field: 'licenseCount',
                headerName: 'Antall lisens(kunde)',
                minWidth: 170,
                flex: 0.9,
                renderCell: (params) => (
                    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-start' }}>
                        <Typography component="span">{params.value}</Typography>
                    </Box>
                ),
            },
            {
                field: 'userCount',
                headerName: 'Antall bruker',
                minWidth: 140,
                flex: 0.8,
            },
        ],
        [],
    );

    const headings = JSON.stringify(['Næringskode', 'Antall firma', 'Antall lisens(kunde)', 'Antall bruker']);
    const dataSet = JSON.stringify(rows.map((x) => [`${x.code} ${x.description}`, x.companyCount, x.licenseCount, x.userCount]));

    if (selectedCode) {
        return (
            <EmployeeReport
                allFlag={false}
                code={selectedCode.code}
                description={selectedCode.description}
                filterOption={props.filterOption}
                onFilterChange={props.onFilterChange}
                onBack={() => setSelectedCode(null)}
            />
        );
    }

    return (
        <ReportWorkspace
            title="Næringskode"
            subtitle="Start i kodeoversikten og klikk antall firma for å gå videre til ansatteintervall."
            onBack={props.onBackToOverview}
            breadcrumb={['BRREG', 'Næringskode']}
            filterOption={props.filterOption}
            onFilterChange={props.onFilterChange}
            excelExport={<GeneralExcelExport dataSet={dataSet} headings={headings} workBookName="BRREG_ANTALL" fileName="brregantall.xlsx" />}>
            <Paper elevation={1} sx={{ p: 2 }}>
                <Stack spacing={2}>
                    <TextField
                        label="Søk etter kode eller beskrivelse"
                        size="small"
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}
                        sx={{ maxWidth: 360 }}
                    />
                    {loading && <LoadingSpinner />}
                    {!loading && (
                        <BrregDataGrid
                            rows={rows}
                            columns={columns}
                            getRowId={(row) => row.code}
                            pageSize={50}
                            pageSizeOptions={[25, 50, 100]}
                        />
                    )}
                </Stack>
            </Paper>
        </ReportWorkspace>
    );
}

interface EmployeeReportProps {
    code: string;
    description?: string;
    allFlag: boolean;
    filterOption: string;
    onFilterChange: (value: string) => void;
    onBack: () => void;
}

function EmployeeReport({ code, description, allFlag, filterOption, onFilterChange, onBack }: EmployeeReportProps) {
    const [selectedRange, setSelectedRange] = useState<string | null>(null);

    const { loading, data } = useQuery<{ employees: EmployeeModel[] }>(GET_EMPLOYEE, {
        variables: { code: code, filterOption: filterOption },
    });

    const headings = JSON.stringify(['Antall ansatte', 'Antall firma', 'Antall lisens(kunde)', 'Antall bruker']);
    const dataSet = JSON.stringify((data?.employees ?? []).map((x) => [x.key, x.companyCount, x.licenseCount, x.userCount]));

    const columns = useMemo<GridColDef[]>(
        () => [
            { field: 'key', headerName: 'Antall ansatte', minWidth: 180, flex: 1 },
            { field: 'companyCount', headerName: 'Antall firma', minWidth: 140, flex: 1 },
            {
                field: 'licenseCount',
                headerName: 'Antall lisens(kunde)',
                minWidth: 170,
                flex: 1,
                renderCell: (params) => (
                    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-start' }}>
                        <Button
                            size="small"
                            onClick={() => setSelectedRange(params.row.key)}
                            sx={{
                                minWidth: 0,
                                px: 0,
                                textDecoration: 'underline',
                                fontWeight: 600,
                            }}>
                            {params.value}
                        </Button>
                    </Box>
                ),
            },
            { field: 'userCount', headerName: 'Antall bruker', minWidth: 130, flex: 1 },
        ],
        [],
    );

    if (selectedRange) {
        return (
            <CompanyReport
                employeeRange={selectedRange}
                code={code}
                description={description}
                filterOption={filterOption}
                withCode={true}
                title={allFlag ? 'Antall ansatte' : 'Næringskode'}
                breadcrumb={allFlag ? ['BRREG', 'Antall ansatte', selectedRange] : ['BRREG', 'Næringskode', code, selectedRange]}
                onBack={() => setSelectedRange(null)}
            />
        );
    }

    return (
        <ReportWorkspace
            title="Antall ansatte"
            subtitle={allFlag ? 'Oversikt for alle næringskoder.' : `Næringskode ${code} ${description ?? ''}`}
            onBack={onBack}
            breadcrumb={allFlag ? ['BRREG', 'Antall ansatte'] : ['BRREG', 'Næringskode', code]}
            filterOption={allFlag ? filterOption : undefined}
            onFilterChange={allFlag ? onFilterChange : undefined}
            excelExport={
                <GeneralExcelExport dataSet={dataSet} headings={headings} workBookName="BRREG_ANSATTE_ANTALL" fileName="brregansatteantall.xlsx" />
            }>
            <Paper elevation={1} sx={{ p: 2 }}>
                {loading && <LoadingSpinner />}
                {!loading && (
                    <BrregDataGrid
                        rows={data?.employees ?? []}
                        columns={columns}
                        getRowId={(row) => row.key}
                        pageSize={50}
                        pageSizeOptions={[25, 50, 100]}
                    />
                )}
            </Paper>
        </ReportWorkspace>
    );
}

function IncorporationCompanyReport(props: { incorporationYearRange: string; filterOption: string; onBack: () => void }) {
    const { loading, data } = useQuery<{ inccompanies: IncorporationYearCompanyModel[] }>(GET_INCORP_YEAR_DETAIL_ALL, {
        variables: {
            incorporationYearRange: props.incorporationYearRange,
            noCodeFilterOption: props.filterOption,
        },
    });

    const headings = JSON.stringify(['Næringskode', 'Firma', 'Org. nummer', 'Kundenummer (Maconomy)', 'Stiftelsesdato']);
    const dataSet = JSON.stringify(
        (data?.inccompanies ?? []).map((x) => [x.code + ' ' + x.codeDescription, x.companyName, x.orgNumber, x.maconomyCustomerNumber, x.incorporationDate]),
    );

    const columns = useMemo<GridColDef[]>(
        () => [
            {
                field: 'codeText',
                headerName: 'Næringskode',
                minWidth: 220,
                flex: 1,
                valueGetter: (_, row) => `${row.code} ${row.codeDescription}`,
            },
            { field: 'companyName', headerName: 'Firma', minWidth: 220, flex: 1.2 },
            { field: 'orgNumber', headerName: 'Org. nummer', minWidth: 140, flex: 1 },
            { field: 'maconomyCustomerNumber', headerName: 'Kundenummer (Maconomy)', minWidth: 190, flex: 1 },
            { field: 'incorporationDate', headerName: 'Stiftelsesdato', minWidth: 140, flex: 1 },
        ],
        [],
    );

    return (
        <ReportWorkspace
            title="Stiftelsesår detaljer"
            subtitle={`Detaljoversikt for år/intervall ${props.incorporationYearRange}`}
            onBack={props.onBack}
            breadcrumb={['BRREG', 'Stiftelsesår', props.incorporationYearRange]}
            excelExport={<GeneralExcelExport dataSet={dataSet} headings={headings} workBookName="BRREG_FIRMA_INFO" fileName="brregfirmainfo.xlsx" />}>
            <Paper elevation={1} sx={{ p: 2 }}>
                {loading && <LoadingSpinner />}
                {!loading && (
                    <BrregDataGrid
                        rows={data?.inccompanies ?? []}
                        columns={columns}
                        getRowId={(row, index) => `${row.orgNumber}-${index}`}
                        pageSize={50}
                        pageSizeOptions={[25, 50, 100]}
                    />
                )}
            </Paper>
        </ReportWorkspace>
    );
}

interface CompanyReportProps {
    employeeRange: string;
    code: string;
    description?: string;
    filterOption: string;
    withCode: boolean;
    title: string;
    breadcrumb: string[];
    onBack: () => void;
}

function CompanyReport({ employeeRange, code, description, filterOption, withCode, title, breadcrumb, onBack }: CompanyReportProps) {
    const { loading, data } = useQuery<{ companies: CompanyModel[] }>(GET_COMPANY, {
        variables: { employeeRange: employeeRange, code: code, filterOption: filterOption },
    });

    const headings = JSON.stringify(['Næringskode', 'Firma', 'Org. nummer', 'Kundenummer (Maconomy)', 'Antall bruker', 'Opprettet']);
    const dataSet = JSON.stringify(
        (data?.companies ?? []).map((x) => [
            x.code + ' ' + x.codeDescription,
            x.companyName,
            x.orgNumber,
            x.maconomyCustomerNumber,
            x.userCount,
            x.createDate,
        ]),
    );

    const columns = useMemo<GridColDef[]>(() => {
        const allColumns: GridColDef[] = [
            {
                field: 'codeText',
                headerName: 'Næringskode',
                minWidth: 230,
                flex: 1,
                valueGetter: (_, row) => `${row.code} ${row.codeDescription}`,
            },
            { field: 'companyName', headerName: 'Firma', minWidth: 220, flex: 1.2 },
            { field: 'orgNumber', headerName: 'Org. nummer', minWidth: 140, flex: 1 },
            { field: 'maconomyCustomerNumber', headerName: 'Kundenummer (Maconomy)', minWidth: 190, flex: 1 },
            { field: 'userCount', headerName: 'Antall bruker', minWidth: 130, flex: 1 },
            { field: 'createDate', headerName: 'Opprettet', minWidth: 130, flex: 1 },
        ];

        if (withCode) {
            return allColumns;
        }

        return allColumns.filter((column) => column.field !== 'codeText');
    }, [withCode]);

    return (
        <ReportWorkspace
            title={`${title} detaljer`}
            subtitle={`Antall ansatte: ${employeeRange}${code ? ` • Næringskode: ${code} ${description ?? ''}` : ''}`}
            onBack={onBack}
            breadcrumb={breadcrumb}
            excelExport={<GeneralExcelExport dataSet={dataSet} headings={headings} workBookName="BRREG_FIRMA_INFO" fileName="brregfirmainfo.xlsx" />}>
            <Paper elevation={1} sx={{ p: 2 }}>
                {loading && <LoadingSpinner />}
                {!loading && (
                    <BrregDataGrid
                        rows={data?.companies ?? []}
                        columns={columns}
                        getRowId={(row, index) => `${row.orgNumber}-${index}`}
                        pageSize={50}
                        pageSizeOptions={[25, 50, 100]}
                    />
                )}
            </Paper>
        </ReportWorkspace>
    );
}

function BrregDataGrid(props: {
    rows: any[];
    columns: GridColDef[];
    getRowId: (row: any, index?: number) => string | number;
    pageSize: number;
    pageSizeOptions: number[];
}) {
    return (
        <DataGridPro
            autoHeight
            density="compact"
            rows={props.rows}
            columns={props.columns}
            getRowId={props.getRowId}
            localeText={gridLocaleText}
            pagination
            pageSizeOptions={props.pageSizeOptions}
            initialState={{
                pagination: {
                    paginationModel: {
                        pageSize: props.pageSize,
                        page: 0,
                    },
                },
            }}
            sx={{
                '& .MuiDataGrid-columnHeaderTitle': {
                    fontWeight: 700,
                },
                '& .MuiDataGrid-cell': {
                    alignItems: 'center',
                },
            }}
        />
    );
}

export interface GeneralExcelExportProps {
    dataSet: string;
    headings: string;
    workBookName: string;
    fileName: string;
}

export function GeneralExcelExport(props: GeneralExcelExportProps) {
    const { dataSet, headings, workBookName, fileName } = props;

    const exportFile = () => {
        const workbook = XLSX.utils.book_new();
        const inputHeading = JSON.parse(headings);
        const finalData = JSON.parse(dataSet);
        const input = [inputHeading, ...finalData];
        const sheet = XLSX.utils.aoa_to_sheet(input);
        XLSX.utils.book_append_sheet(workbook, sheet, workBookName);
        XLSX.writeFile(workbook, fileName);
    };

    return (
        <div style={{ textAlign: 'right' }}>
            <IconButton onClick={exportFile} title="Eksport til Excel" size="small">
                <ExcelLogo
                    style={{
                        width: 20,
                        height: 20,
                    }}
                />
            </IconButton>
        </div>
    );
}

function IncorporationYearReport(props: { filterOption: string; onFilterChange: (value: string) => void; onBack: () => void }) {
    const [selectedYearRange, setSelectedYearRange] = useState<string | null>(null);

    const { loading, data } = useQuery<{ incorporationscomp: IncorporationYearModel[] }>(GET_INCORP_YEAR_ALL, {
        variables: { noCodeFilterOption: props.filterOption },
    });

    const headings = JSON.stringify(['Stiftelsesår', 'Antall firma', 'Antall kunder (lisenser)']);
    const dataSet = JSON.stringify((data?.incorporationscomp ?? []).map((x) => [x.key, x.companyCount, x.licenseCount]));

    const columns = useMemo<GridColDef[]>(
        () => [
            { field: 'key', headerName: 'Stiftelsesår', minWidth: 180, flex: 1 },
            { field: 'companyCount', headerName: 'Antall firma', minWidth: 140, flex: 1 },
            {
                field: 'licenseCount',
                headerName: 'Antall lisens(kunde)',
                minWidth: 180,
                flex: 1,
                renderCell: (params) => (
                    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-start' }}>
                        <Button
                            size="small"
                            onClick={() => setSelectedYearRange(params.row.key)}
                            sx={{
                                minWidth: 0,
                                px: 0,
                                textDecoration: 'underline',
                                fontWeight: 600,
                            }}>
                            {params.value}
                        </Button>
                    </Box>
                ),
            },
        ],
        [],
    );

    if (selectedYearRange) {
        return (
            <IncorporationCompanyReport
                incorporationYearRange={selectedYearRange}
                filterOption={props.filterOption}
                onBack={() => setSelectedYearRange(null)}
            />
        );
    }

    return (
        <ReportWorkspace
            title="Stiftelsesår"
            subtitle="Se fordeling per år, og åpne firmadetaljer for valgt år."
            onBack={props.onBack}
            breadcrumb={['BRREG', 'Stiftelsesår']}
            filterOption={props.filterOption}
            onFilterChange={props.onFilterChange}
            excelExport={
                <GeneralExcelExport
                    dataSet={dataSet}
                    headings={headings}
                    workBookName="BRREG_STIFTELSES_AAR_ANTALL"
                    fileName="brregstiftelsesaarantall.xlsx"
                />
            }>
            <Paper elevation={1} sx={{ p: 2 }}>
                {loading && <LoadingSpinner />}
                {!loading && (
                    <BrregDataGrid
                        rows={data?.incorporationscomp ?? []}
                        columns={columns}
                        getRowId={(row) => row.key}
                        pageSize={50}
                        pageSizeOptions={[25, 50, 100]}
                    />
                )}
            </Paper>
        </ReportWorkspace>
    );
}